import type { ObjectTypeDefinition } from "daedalus-core";
import { UNLOCKED_GUARD } from "./lock.ts";
import { isServerVerb, offersNoVerb } from "./verbs.ts";

/**
 * Which states a player can bring a thing to (ADR-096's amendment of 2026-10-13, items 1 to 3). The compile's
 * shrine reachability check reads it to know which things a character can pass: a thing blocks there only the
 * cells every one of these states blocks. Nothing a player meets reads it. Pure.
 */

/**
 * The guards a transition may require that a player can meet: `unlocked`, met by spending a gate's key. A guard
 * not listed here is read as never met, so a transition a new guard is put on is one no player makes until the
 * guard is added.
 */
export const PLAYER_MET_GUARDS: readonly string[] = [UNLOCKED_GUARD];

/**
 * The states a player can bring a thing of a type to: its initial state and every state reached from it by
 * transitions that are a player's to make. A transition is a player's when its type offers verbs (not a
 * training dummy, which blows drive, nor a shrine), its verb is not the map server's alone (a chest's `shut`)
 * and every guard it requires is one a player can meet (`PLAYER_MET_GUARDS`).
 *
 * @param type The object type.
 * @returns The state ids, in the order the type declares its states; the initial state alone for a type that
 *   offers no verb.
 * @example
 * statesAPlayerReaches(gate);
 * // ["closed", "open"]
 */
export function statesAPlayerReaches(type: ObjectTypeDefinition): string[] {
  const reached = new Set<string>([type.initial]);
  if (!offersNoVerb(type)) {
    const queue = [type.initial];
    for (let head = 0; head < queue.length; head += 1) {
      const from = queue[head] as string;
      for (const transition of type.transitions) {
        if (transition.from !== from || reached.has(transition.to) || isServerVerb(type, transition.verb) || !(transition.requires ?? []).every((guard) => PLAYER_MET_GUARDS.includes(guard))) {
          continue;
        }
        reached.add(transition.to);
        queue.push(transition.to);
      }
    }
  }
  return type.states.map((state) => state.id).filter((id) => reached.has(id));
}
